import { DEPOSIT_TRANSFER_TYPE } from '@ovComponents/4-module/workflowCompletion/constants/deposits';
import { UserContext } from '@providers/userContextProvider';
import { pick } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { invalidFields } from '../utils';
import { buildDepositFieldOptions } from '../utils/depositFieldOptions';
import { useDepositSchedule } from '../utils/useDepositSchedule';
import { useDepositWorkflowData } from '../utils/useDepositWorkflowData';
import type { MultiPortfolioDepositProps } from './multiPortfolioDeposit';
import { useDepositEligibleSubAccounts } from './useDepositEligibleSubAccounts';

type UseMultiPortfolioDepositProp = Pick<
  MultiPortfolioDepositProps,
  | 'options'
  | 'onNext'
  | 'workflowCompletion'
  | 'dummyDeposit'
  | 'setDummyDeposit'
>;

const VALIDATED_FIELDS = ['amountCents', 'bankAccount'];

const EFT_SCHEDULE_FIELDS = ['schedule', 'scheduledDate'];

const validatedFieldsFor = (isEft: boolean) =>
  isEft ? [...VALIDATED_FIELDS, ...EFT_SCHEDULE_FIELDS] : VALIDATED_FIELDS;

export const useMultiPortfolioDeposit = ({
  options,
  onNext,
  workflowCompletion,
  dummyDeposit,
  setDummyDeposit,
}: UseMultiPortfolioDepositProp) => {
  const { userId: paramsUserId } = useParams();
  const { activeEntity } = useContext(UserContext);
  const userId = paramsUserId ?? activeEntity?.id;

  const [accountId, setAccountId] = useState('');
  const [focused, setFocused] = useState<string[]>([]);

  const { accounts, loading } = useDepositEligibleSubAccounts(userId);

  const selectedEligibleAccount = accounts.find(
    ({ account }) => account.id === accountId,
  );

  const { applicableData: depositData, updateContextWithFields } =
    useDepositWorkflowData(dummyDeposit, setDummyDeposit, workflowCompletion);

  useEffect(() => {
    if (depositData?.type || !options?.defaultTransferType) return;
    updateContextWithFields({ type: options.defaultTransferType });
  }, []);

  const isEft = depositData?.type === DEPOSIT_TRANSFER_TYPE.EFT;

  const schedule = useDepositSchedule({
    options,
    deposit: depositData,
    updateContextWithFields,
    userId,
  });

  const missingFields = invalidFields(
    pick(
      buildDepositFieldOptions({
        options: options ?? {},
        applicableData: depositData,
        starting: schedule.starting,
        participantNumberEnabled: false,
        isMultiCurrencyInternalTransfer: false,
        hasAvailableCurrencies: false,
      }),
      validatedFieldsFor(isEft),
    ),
    depositData,
  );

  const focus = (field: string) => setFocused([...focused, field]);

  const showsFieldError = (field: string) =>
    focused.includes(field) && missingFields.includes(field);

  const selectAccount = (id: string) => {
    setAccountId(id);
    updateContextWithFields({ bankAccount: undefined });
  };

  const submit = async () => {
    if (missingFields.length > 0) {
      setFocused(missingFields);
      return;
    }
    if (!schedule.validTransferDelayCheck) {
      setFocused(['scheduledDate']);
      return;
    }
    await onNext();
  };

  return {
    userId,
    accounts,
    loading,
    accountId,
    selectedAccount: selectedEligibleAccount?.account,
    subAccounts: selectedEligibleAccount?.subAccounts ?? [],
    depositData,
    updateContextWithFields,
    isEft,
    schedule,
    focused,
    focus,
    showsFieldError,
    selectAccount,
    submit,
  };
};
